import { TextMask } from '@app/interfaces';
import { Locale } from '@app/types/translatable.type';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

const SIN_DIGITS = 9;
const BANK_INSTITUTION_DIGITS = 3;
const BANK_TRANSIT_DIGITS = 5;
const BANK_ACCOUNT_DIGITS = 12;

const digit = /\d/;
const placeholderChar = '\u2000';

const postalCodeMask: Array<RegExp | string> = [/[A-Z]/i, digit, /[A-Z]/i, ' ', digit, /[A-Z]/i, digit];
const phoneNumberMask: Array<RegExp | string> = [
    '(',
    /[1-9]/,
    digit,
    digit,
    ')',
    ' ',
    digit,
    digit,
    digit,
    '-',
    digit,
    digit,
    digit,
    digit,
];

const upperCasePipe = (conformedValue: string): string => conformedValue.toUpperCase();

export const makePhoneNumberMask = (): TextMask => ({
    mask: phoneNumberMask,
    placeholderChar,
    guide: true,
});

export const makePostalCodeMask = (): TextMask => ({
    mask: postalCodeMask,
    placeholderChar,
    guide: true,
    pipe: upperCasePipe,
});

export const makePercentageMask = (locale: Locale = 'en_CA'): TextMask =>
    createNumberMask({
        prefix: '',
        suffix: locale === 'en_CA' ? '%' : ' %',
        includeThousandsSeparator: true,
        allowDecimal: true,
        decimalSymbol: locale === 'en_CA' ? '.' : ',',
        thousandsSeparatorSymbol: locale === 'en_CA' ? ',' : ' ',
        guide: true,
        keepCharPositions: false,
        integerLimit: 2,
    });

// French numbers use space for thousands and comma instead of a decimal before cents.
// This takes an en_CA number (identified by a decimal point) and converts it to fr_CA format.
// The split at the decimal separates what will be the dollars and cents. The dollars have
// any commas replaced by spaces for thousands, and the decimal part is truncated to decimalLimit
// places before they are joined back together with a comma between them.
const makeFrCAPastePipe =
    (decimalLimit: number) =>
    (conformedValue: string, { rawValue }: { rawValue: string }): string => {
        if (rawValue.includes('.')) {
            const parts = rawValue.replace(/[^0-9.]/g, '').split('.');
            if (parts.length === 2) {
                const dollars = (parts[0] || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                return parts[1].length > 0 ? `${dollars},${parts[1].slice(0, decimalLimit)}` : `${dollars},`;
            }
        }
        return conformedValue;
    };

export const makeTwoDecimalPointsNumberMask = (locale: Locale = 'en_CA'): TextMask => ({
    mask: createNumberMask({
        prefix: '',
        allowDecimal: true,
        decimalSymbol: locale === 'fr_CA' ? ',' : '.',
        thousandsSeparatorSymbol: locale === 'fr_CA' ? ' ' : ',',
        decimalLimit: 2,
    }),
    pipe: locale === 'fr_CA' ? makeFrCAPastePipe(2) : undefined,
});

export const makeCustomDecimalNumberMask = (locale: Locale = 'en_CA', decimalLimit: number | null = null): TextMask =>
    createNumberMask({
        prefix: '',
        allowDecimal: true,
        decimalLimit: decimalLimit,
        includeThousandsSeparator: false,
        integerLimit: null,
        decimalSymbol: locale === 'fr_CA' ? ',' : '.',
    });

export const makeCurrencyMask = (
    {
        keepCharPositions,
        allowDecimal,
    }: Partial<{
        keepCharPositions: boolean;
        allowDecimal: boolean;
    }> = {},
    locale: Locale = 'en_CA'
): TextMask['mask'] =>
    createNumberMask({
        prefix: locale === 'fr_CA' ? '' : '$',
        suffix: locale === 'fr_CA' ? ' $' : '',
        thousandsSeparatorSymbol: locale === 'fr_CA' ? ' ' : ',',
        decimalSymbol: locale === 'fr_CA' ? ',' : '.',
        includeThousandsSeparator: true,
        allowDecimal: allowDecimal ?? true,
        guide: true,
        keepCharPositions: keepCharPositions ?? false,
    });

export const makeCurrencyTextMask = (
    options: Partial<{ keepCharPositions: boolean; allowDecimal: boolean }> = {},
    locale: Locale = 'en_CA'
): TextMask => ({
    mask: makeCurrencyMask(options, locale),
    pipe: locale === 'fr_CA' ? makeFrCAPastePipe(2) : undefined,
});

export const sinNumberMask: TextMask = {
    mask: Array(SIN_DIGITS).fill(digit),
    placeholderChar,
    guide: false,
};

export const bankInstitutionMask: TextMask = {
    mask: Array(BANK_INSTITUTION_DIGITS).fill(digit),
    placeholderChar,
    guide: false,
};

export const bankTransitMask: TextMask = {
    mask: Array(BANK_TRANSIT_DIGITS).fill(digit),
    placeholderChar,
    guide: false,
};

export const bankAccountMask: TextMask = {
    mask: Array(BANK_ACCOUNT_DIGITS).fill(digit),
    placeholderChar,
    guide: false,
};
